
import React, { useEffect, useState } from 'react';
import { useAuth } from './AuthProvider';
import { LocationRestriction } from '../LocationRestriction';
import { SmsAuth } from './SmsAuth';
import { Button } from '@/components/ui/button';
import { Store } from 'lucide-react';

interface AuthGuardProps {
  children: React.ReactNode;
  onStoreStationAccess?: () => void;
  allowStaffAccess?: boolean;
}

export const AuthGuard = ({ children, onStoreStationAccess, allowStaffAccess }: AuthGuardProps) => {
  const { user, session, loading, isRecoverySession } = useAuth();
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const checkAuthentication = () => {
      console.log('🔍 AuthGuard checking authentication state...');
      console.log('Loading:', loading, 'User:', !!user, 'Session:', !!session);
      
      // If AuthProvider is still loading, don't make auth decisions yet
      if (loading) {
        console.log('⏳ AuthProvider still loading, waiting...');
        return;
      }
      
      let authStatus = false;
      
      // SECURITY: Block recovery sessions from general access
      if (isRecoverySession) {
        console.log('🔒 Recovery session detected - blocking general access');
        authStatus = false;
      }
      // Check Supabase authentication - session is sufficient
      else if (session) {
        console.log('✅ Authenticated via Supabase session');
        authStatus = true;
      }
      // SECURITY: Check SMS authentication (staff-level only)
      else {
        const smsAuth = localStorage.getItem('staff_sms_auth');
        
        if (smsAuth) {
          try {
            const authData = JSON.parse(smsAuth);
            if (authData.authenticated && authData.expiry > new Date().getTime()) {
              console.log('✅ Authenticated via SMS (staff level)');
              authStatus = true;
            } else {
              console.log('❌ SMS auth expired, cleaning up');
              localStorage.removeItem('staff_sms_auth');
            }
          } catch (error) {
            console.error('Error parsing SMS auth:', error);
            localStorage.removeItem('staff_sms_auth');
          }
        }
        
        // SECURITY: Remove sms_admin_auth - no longer supported
        const oldAdminAuth = localStorage.getItem('sms_admin_auth');
        if (oldAdminAuth) {
          console.log('🔒 Removing deprecated SMS admin auth');
          localStorage.removeItem('sms_admin_auth');
        }
      }
      
      console.log('🔄 Setting authentication status:', authStatus);
      setIsAuthenticated(authStatus);
    };

    checkAuthentication();
  }, [user, session, loading]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="relative">
        <SmsAuth onAuthSuccess={() => {
          setIsAuthenticated(true);
        }} />
        {onStoreStationAccess && (
          <div className="fixed top-4 right-4 z-50">
            <Button
              onClick={onStoreStationAccess}
              variant="outline"
              size="sm"
              className="bg-white/90 backdrop-blur-sm hover:bg-white/100 border-gray-300"
            >
              <Store className="w-4 h-4 mr-2" />
              Store Station
            </Button>
          </div>
        )}
      </div>
    );
  }

  return <>{children}</>;
};
